import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageTitle } from "../components/site/PageTitle";

export default function LoginPage() {
  const { user, verify } = useAuth();
  const [params] = useSearchParams();
  const isAdmin = params.get("admin") === "1";
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : "/account", { replace: true });
  }, [user, nav]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.requestCode(email);
      setSent(true);
      setCode("");
      setWait(30);
      toast.success(`Code sent to ${email}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verify(email, code);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageTitle kicker={isAdmin ? "Staff only" : "Account"} title={isAdmin ? "Admin login" : "Log in"}>
        {sent ? `Enter the 6-digit code we emailed to ${email}.` : "Enter your email and we'll send you a login code. No password needed."}
      </PageTitle>
      {!sent ? (
        <form onSubmit={send} className="mx-5 lg:mx-12 max-w-md card-surface rounded-2xl p-8 space-y-5" data-testid="login-email-form">
          <input className="field" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" autoFocus />
          <button type="submit" disabled={busy} data-testid="login-send-code-button" className="btn-lime w-full rounded-full py-3.5 font-cond uppercase font-bold tracking-widest text-lg flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Email me a code
          </button>
        </form>
      ) : (
        <form onSubmit={check} className="mx-5 lg:mx-12 max-w-md card-surface rounded-2xl p-8 space-y-5" data-testid="login-code-form">
          <input className="field text-center font-mono-ui text-2xl tracking-[0.5em]" inputMode="numeric" autoComplete="one-time-code" required maxLength={6}
            placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="login-code-input" autoFocus />
          <button type="submit" disabled={busy || code.length !== 6} data-testid="login-verify-button" className="btn-lime w-full rounded-full py-3.5 font-cond uppercase font-bold tracking-widest text-lg flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Log in
          </button>
          <div className="flex justify-between text-sm">
            <button type="button" onClick={() => setSent(false)} className="text-slate-400 hover:text-lime" data-testid="login-change-email">Use a different email</button>
            <button type="button" onClick={send} disabled={busy || wait > 0} className="text-lime disabled:text-slate-500" data-testid="login-resend-code">{wait > 0 ? `Resend in ${wait}s` : "Resend code"}</button>
          </div>
        </form>
      )}
    </>
  );
}
